/**
 * @fileoverview One program, as a viewer is allowed to see it.
 *
 * Laid out like the org Dashboard, cut to what applies to someone who looks
 * at a program rather than runs it (lib/viewerPortal): the vital signs as
 * tiles under the title, each opening the tab behind its number, then a
 * URL-backed tab bar.
 *
 * - **Overview** — the classes (when, where, who, how many; attendance inside
 *   each, closed to start), the instructors, the latest lesson notes and the
 *   program's details.
 * - **Lessons** — every session, sortable, exportable.
 * - **Students** — the roster, and uploading one.
 * - **Calendar** — the program's meetings, a week at a time.
 * - **Settings** — their emails, the program's contact, leaving.
 *
 * Every section is still its own grant: the server leaves out what was not
 * granted rather than sending it empty, and a tab with nothing it could show
 * is not offered. The program carries the organization's logo beside its
 * title, the same mark as the shell's corner, which a phone's top bar does
 * not show.
 */

import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { OrgAuthContext } from "@/App";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { useTabParam } from "@/lib/tabParam";
import { Icon, PageShell, SegmentedControl, StatCard } from "@/components/studio/primitives";
import { OrgLogoTile, orgIdentity } from "@/components/org/OrgLayout";
import ViewerOverview from "@/components/org/viewer/ViewerOverview";
import ViewerLessons from "@/components/org/viewer/ViewerLessons";
import ViewerStudents from "@/components/org/viewer/ViewerStudents";
import ViewerCalendar from "@/components/org/viewer/ViewerCalendar";
import ViewerSettings from "@/components/org/viewer/ViewerSettings";
import { VIEWER_TABS, longDate, plural, viewerTabs } from "@/lib/viewerPortal";

/**
 * The vital signs: sessions held and to come, students enrolled, the next seven
 * days. Counted by the Programs tab's own rule; where Hans cannot see every
 * session (a class Scheduler publishes, private lessons) the tiles say so, as
 * the dashboard's Upcoming tile does.
 */
function Tiles({ summary, has, onOpen }) {
  const partial = summary.complete === false ? " · Hans classes only" : "";
  return (
    <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="viewer-tiles">
      <StatCard
        compact
        value={summary.sessions_held.toLocaleString()}
        label="Total sessions"
        sub={`Held so far${summary.sessions_remaining ? ` · ${summary.sessions_remaining.toLocaleString()} to come` : ""}${partial}`}
        onPress={has("lessons") ? () => onOpen("lessons") : undefined}
        data-testid="viewer-tile-sessions"
      />
      <StatCard
        compact
        value={summary.students_enrolled.toLocaleString()}
        label="Students enrolled"
        sub={`In ${plural(summary.classes, "class", "classes")}`}
        onPress={has("students") ? () => onOpen("students") : undefined}
        data-testid="viewer-tile-students"
      />
      <StatCard
        compact
        value={summary.week_ahead.count.toLocaleString()}
        label="Week ahead"
        sub={`${summary.week_ahead.label}${partial}`}
        onPress={() => onOpen("calendar")}
        data-testid="viewer-tile-week"
      />
    </div>
  );
}

export default function OrgViewerProgramPage() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { org, admin, logout } = useContext(OrgAuthContext) || {};
  const { tab: requested, filter, setTab } = useTabParam(VIEWER_TABS, "overview");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await orgApi.get(`/org/viewer/programs/${programId}`);
      setData(res.data);
    } catch (err) {
      setError(getOrgErrorMessage(err, "Couldn't load this program"));
    }
  }, [programId]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return <PageShell width="wide" className="font-sans text-[15px] text-[var(--text-tertiary)]">{error}</PageShell>;
  }
  if (!data) {
    return <PageShell width="wide" className="font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</PageShell>;
  }

  const { program, permissions = [], summary } = data;
  const tabs = viewerTabs(permissions);
  const has = (id) => tabs.some((t) => t.id === id);
  const tab = has(requested) ? requested : "overview";
  // The organization as its own portal shows it — the shell's corner, from the
  // same record. Every program a viewer sees is their one organization's.
  const identity = orgIdentity(org);
  const orgName = org ? identity.name : program.org_name;
  // One program is the whole portal: there is no list to go back to.
  const several = (admin?.program_ids || []).length > 1;

  const onLeft = (result) => {
    toast.success(`You've left ${program.title}`);
    if (result.account_closed) logout?.();
    else navigate("/org/viewer");
  };

  return (
    <PageShell width="wide" data-testid="viewer-program">
      {several && (
        <button
          type="button"
          onClick={() => navigate("/org/viewer")}
          className="mb-4 inline-flex items-center gap-1 font-sans text-[13px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
          data-testid="viewer-back"
        >
          <Icon name="chevronLeft" size={15} /> Your programs
        </button>
      )}
      <div className="mb-6 flex items-center gap-4" data-testid="viewer-program-header">
        {identity.logoUrl && <OrgLogoTile logoUrl={identity.logoUrl} className="h-12 w-12" testId="viewer-program-logo" />}
        <div className="min-w-0">
          <h1 className="font-serif text-2xl font-semibold text-[var(--text-primary)]">{program.title}</h1>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 font-sans text-[13px] text-[var(--text-tertiary)]">
            {orgName && <span data-testid="viewer-program-org">{orgName}</span>}
            {program.location && <span>{program.location}</span>}
            {program.start_date && (
              <span>
                {longDate(program.start_date)}
                {program.end_date ? ` – ${longDate(program.end_date)}` : ""}
              </span>
            )}
          </div>
        </div>
      </div>

      {summary && <Tiles summary={summary} has={has} onOpen={(id) => setTab(id)} />}

      {/* Five tabs are wider than a phone: the bar scrolls in its own row. */}
      <div className="-mx-4 mb-5 min-w-0 overflow-x-auto px-4 md:mx-0 md:px-0">
        <SegmentedControl testId="viewer-tabs" value={tab} onChange={(id) => setTab(id)} options={tabs} />
      </div>

      {tab === "overview" && <ViewerOverview data={data} onOpenTab={(id) => setTab(id)} />}
      {tab === "lessons" && (
        <ViewerLessons
          programId={programId}
          permissions={permissions}
          filter={filter}
          onFilter={(f) => setTab("lessons", f)}
          title={program.title}
        />
      )}
      {tab === "students" && <ViewerStudents data={data} programId={programId} onImported={load} />}
      {tab === "calendar" && <ViewerCalendar programId={programId} classes={data.classes || []} />}
      {tab === "settings" && (
        <ViewerSettings programId={programId} programTitle={program.title} orgName={orgName} onLeft={onLeft} />
      )}
    </PageShell>
  );
}
